import React from 'react';
import { motion } from 'framer-motion';

// Variants for different animation types
const variants = {
  fadeUp: {
    hidden: { opacity: 0, y: 40 },
    visible: { opacity: 1, y: 0 }
  },
  fadeDown: {
    hidden: { opacity: 0, y: -40 },
    visible: { opacity: 1, y: 0 }
  },
  fadeLeft: {
    hidden: { opacity: 0, x: -40 },
    visible: { opacity: 1, x: 0 }
  },
  fadeRight: {
    hidden: { opacity: 0, x: 40 },
    visible: { opacity: 1, x: 0 }
  },
  fadeIn: {
    hidden: { opacity: 0 },
    visible: { opacity: 1 }
  },
  scale: {
    hidden: { opacity: 0, scale: 0.9 },
    visible: { opacity: 1, scale: 1 }
  }
};

/**
 * AnimatedSection - A reusable component for scroll-triggered animations
 * 
 * @param {string} animation - Type of animation: 'fadeUp', 'fadeDown', 'fadeLeft', 'fadeRight', 'fadeIn', 'scale'
 * @param {number} delay - Delay before animation starts (in seconds)
 * @param {number} duration - Duration of the animation (in seconds)
 * @param {string} className - Additional CSS classes
 * @param {React.ReactNode} children - Content to animate
 * @param {boolean} once - Whether to animate only once (default: true)
 */
const AnimatedSection = ({
  children,
  animation = 'fadeUp',
  delay = 0,
  duration = 0.6,
  className = '',
  once = true,
  threshold = 0.1,
  as = 'div',
  ...props
}) => {
  const MotionComponent = motion[as] || motion.div;

  return (
    <MotionComponent
      initial="hidden"
      whileInView="visible"
      viewport={{ once, amount: threshold }}
      variants={variants[animation] || variants.fadeUp}
      transition={{
        duration,
        delay,
        ease: [0.25, 0.1, 0.25, 1] // Custom easing for smooth luxury feel
      }}
      className={className}
      {...props}
    >
      {children}
    </MotionComponent>
  );
};

/**
 * AnimatedText - For staggered text animations
 */
export const AnimatedText = ({
  children,
  delay = 0,
  className = '',
  staggerDelay = 0.1
}) => {
  const words = typeof children === 'string' ? children.split(' ') : [children];

  return (
    <motion.span className={className}>
      {words.map((word, i) => (
        <motion.span
          key={i}
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{
            duration: 0.5,
            delay: delay + i * staggerDelay,
            ease: [0.25, 0.1, 0.25, 1]
          }}
          style={{ display: 'inline-block', marginRight: '0.25em' }}
        >
          {word}
        </motion.span>
      ))}
    </motion.span>
  );
};

/**
 * StaggerChildren - Container for staggered child animations
 */
export const StaggerChildren = ({
  children,
  staggerDelay = 0.1,
  className = '',
  ...props
}) => {
  return (
    <motion.div
      initial="hidden"
      whileInView="visible"
      viewport={{ once: true, amount: 0.1 }}
      variants={{
        visible: {
          transition: {
            staggerChildren: staggerDelay
          }
        }
      }}
      className={className}
      {...props}
    >
      {children}
    </motion.div>
  );
};

/**
 * StaggerItem - Child item for StaggerChildren container
 */
export const StaggerItem = ({
  children,
  animation = 'fadeUp',
  className = '',
  ...props
}) => {
  return (
    <motion.div
      variants={variants[animation] || variants.fadeUp}
      transition={{ duration: 0.5, ease: [0.25, 0.1, 0.25, 1] }}
      className={className}
      {...props}
    >
      {children}
    </motion.div>
  );
};

export default AnimatedSection;
